import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    model as modelSignal,
    output,
    untracked,
} from '@angular/core';
import {NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {
    caseInsensitiveComparator,
    composeComparators,
    fromControl,
    includesIgnoreCase,
    keyComparator,
    TranslatePipe,
} from '@brunata-angular/util';
import {
    InputDropdownCheckboxesComponent,
    InputTextGroupComponent,
    SelectableItem,
    SortDirection,
} from '@brunata-angular/ui';
import {SectionToggleComponent} from '../../shared/ui/section-toggle/section-toggle.component';
import {
    SelectionTableColumn,
    SelectionTableComponent,
} from '../../shared/ui/selection-table/selection-table.component';
import {SelectionTableCellDirective} from '../../shared/ui/selection-table/selection-table-templates.directive';
import {MeterModelRow, MeterSuperTypeOption} from './meter-scope.model';

const byVendor = (direction: SortDirection): ((a: MeterModelRow, b: MeterModelRow) => number) =>
    caseInsensitiveComparator<MeterModelRow>(model => model.vendorName ?? '', direction);

const byModel = (direction: SortDirection): ((a: MeterModelRow, b: MeterModelRow) => number) =>
    caseInsensitiveComparator<MeterModelRow>(model => model.modelName, direction);

@Component({
    selector: 'app-vendor-models-panel',
    standalone: true,
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        SectionToggleComponent,
        SelectionTableComponent,
        SelectionTableCellDirective,
    ],
    template: `
        <app-section-toggle [(expanded)]="expanded" controls="vendor-models-panel">
            <span class="fw-bold">
                {{ 'customizedexports_export_step_models_title' | translate: 'online' : 'export' }}
                <span class="text-danger">*</span>
            </span>
            <span class="fw-normal text-muted small">
                {{
                    'customizedexports_export_step_models_hint_short'
                        | translate: 'online' : 'export'
                }}
            </span>
            <span class="fw-normal text-muted small ms-auto">
                {{
                    'customizedexports_export_step_models_selected_count'
                        | translate
                            : 'online'
                            : 'export'
                            : [selectedCount().toString(), models().length.toString()]
                }}
            </span>
        </app-section-toggle>

        @if (expanded()) {
            <div id="vendor-models-panel">
                <form
                    class="d-flex align-items-center flex-wrap gap-3 px-3 pb-3"
                    [formGroup]="filterForm">
                    <app-input-text-group
                        class="search-field"
                        formControlName="query"
                        [placeholder]="
                            'customizedexports_export_step_models_search'
                                | translate: 'online' : 'export'
                        " />
                    <div class="vendor-filter d-flex align-items-center gap-2">
                        <span class="text-muted small">
                            {{
                                'customizedexports_export_step_models_col_vendor'
                                    | translate: 'online' : 'export'
                            }}
                        </span>
                        <app-input-dropdown-checkboxes
                            class="flex-grow-1"
                            formControlName="vendors" />
                    </div>
                </form>

                <app-selection-table
                    [rows]="filteredModels()"
                    [columns]="columns()"
                    sortedBy="superType"
                    [rowLabel]="modelName"
                    [selectAllLabel]="
                        selectAllLabelKey()
                            | translate: 'online' : 'export' : [filteredModels().length.toString()]
                    "
                    [emptyText]="
                        'customizedexports_export_step_models_no_match'
                            | translate: 'online' : 'export'
                    "
                    (rowToggled)="modelToggled.emit($event)"
                    (rowsSelected)="modelsSelected.emit($event)"
                    (rowsDeselected)="modelsDeselected.emit($event)">
                    <ng-template
                        appSelectionTableCell="superType"
                        [appSelectionTableCellOf]="filteredModels()"
                        let-model>
                        {{ model.superTypeNameKey | translate: 'online' : 'common' }}
                    </ng-template>
                    <ng-template
                        appSelectionTableCell="vendor"
                        [appSelectionTableCellOf]="filteredModels()"
                        let-model>
                        {{
                            model.vendorName ??
                                ('customizedexports_export_step_models_unknown_vendor'
                                    | translate: 'online' : 'export')
                        }}
                    </ng-template>
                </app-selection-table>
            </div>
        }
    `,
    styles: `
        .search-field {
            display: block;
            width: 16rem;
            max-width: 100%;
        }

        .vendor-filter {
            min-width: 16rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorModelsPanelComponent {
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly models = input.required<ReadonlyArray<MeterModelRow>>();
    readonly vendorNames = input.required<ReadonlyArray<string>>();
    readonly superTypes = input.required<ReadonlyArray<MeterSuperTypeOption>>();

    readonly modelToggled = output<string>();
    readonly modelsSelected = output<string[]>();
    readonly modelsDeselected = output<string[]>();

    readonly expanded = modelSignal(false);

    protected readonly filterForm = this.formBuilder.group({
        query: this.formBuilder.control(''),
        vendors: this.formBuilder.control<SelectableItem[]>([]),
    });

    private readonly filters = toSignal(fromControl(this.filterForm), {
        initialValue: this.filterForm.getRawValue(),
    });

    protected readonly selectedCount = computed(
        () => this.models().filter(model => model.selected).length,
    );

    protected readonly columns = computed((): SelectionTableColumn<MeterModelRow>[] => [
        {
            key: 'superType',
            labelKey: 'customizedexports_export_step_models_col_super_type',
            hidden: this.superTypes().length <= 1,
            compare: direction =>
                composeComparators<MeterModelRow>([
                    keyComparator<MeterModelRow, number>(
                        model => Number(model.meterSuperTypeNo),
                        direction,
                    ),
                    byVendor(direction),
                    byModel(direction),
                ]),
        },
        {
            key: 'vendor',
            labelKey: 'customizedexports_export_step_models_col_vendor',
            compare: direction =>
                composeComparators<MeterModelRow>([byVendor(direction), byModel(direction)]),
        },
        {
            key: 'model',
            labelKey: 'customizedexports_export_step_models_col_model',
            text: model => model.modelName,
            truncate: true,
            compare: byModel,
        },
        {
            key: 'channels',
            labelKey: 'customizedexports_export_step_models_col_channels',
            text: model => model.channelCount.toString(),
            alignEnd: true,
            compare: direction =>
                keyComparator<MeterModelRow, number>(model => model.channelCount, direction),
        },
    ]);

    private readonly pickedVendorNames = computed(() =>
        (this.filters().vendors ?? []).filter(vendor => vendor.selected).map(vendor => vendor.name),
    );

    // The dropdown's value is its own option list, so the offered vendors have to be written into
    // the control. Filtering by what is both picked and still offered keeps the table and the
    // dropdown telling the same story across a rewrite, which does not re-emit the control.
    private readonly pickedVendors = computed(() => {
        const offered = new Set(this.vendorNames());
        return new Set(this.pickedVendorNames().filter(name => offered.has(name)));
    });

    private readonly narrowed = computed(
        () => (this.filters().query ?? '').trim() !== '' || this.pickedVendors().size > 0,
    );

    protected readonly filteredModels = computed(() => {
        const query = this.filters().query ?? '';
        const vendors = this.pickedVendors();
        return this.models().filter(
            model =>
                (vendors.size === 0 || vendors.has(model.vendorName ?? '')) &&
                (includesIgnoreCase(model.modelName, query) ||
                    includesIgnoreCase(model.vendorName, query)),
        );
    });

    protected readonly selectAllLabelKey = computed(() =>
        this.narrowed()
            ? 'customizedexports_export_step_models_select_all_shown'
            : 'customizedexports_export_step_models_select_all',
    );

    constructor() {
        effect(() => {
            const offered = this.vendorNames();
            const picked = new Set(untracked(this.pickedVendorNames));
            this.filterForm.controls.vendors.setValue(
                offered.map(name => ({name, selected: picked.has(name)})),
                {emitEvent: false},
            );
        });
    }

    protected readonly modelName = (model: MeterModelRow): string => model.modelName;
}
